import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { HistoricalPrescription } from '../types/prescription';
import { getPatientPrescriptions, updatePrescription } from '../api/prescriptions';

export function useHistoricalPrescriptions(patientId: string) {
  const [prescriptions, setPrescriptions] = useState<HistoricalPrescription[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    async function fetchPrescriptions() {
      try {
        setIsLoading(true);
        const data = await getPatientPrescriptions(patientId);
        setPrescriptions(data);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch prescriptions'));
        toast({
          title: 'Error',
          description: 'Failed to load prescriptions',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    }

    fetchPrescriptions();
  }, [patientId, toast]);

  const updatePrescriptionStatus = async (prescriptionId: string, active: boolean) => {
    try {
      const updatedPrescription = await updatePrescription({
        id: prescriptionId,
        active,
      });

      setPrescriptions(current =>
        current.map(p =>
          p.id === prescriptionId ? updatedPrescription : p
        )
      );

      toast({
        title: 'Success',
        description: `Prescription ${active ? 'activated' : 'deactivated'} successfully`,
      });
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to update prescription status',
        variant: 'destructive',
      });
      throw err;
    }
  };

  return {
    prescriptions,
    isLoading,
    error,
    updatePrescriptionStatus,
  };
}
